import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Ban, RotateCcw, Percent, KeyRound, Wallet, Copy, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDateTime, inr } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { RateChart } from "@/pages/dashboard/RateCard";

export default function MerchantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState({ b2c: "", b2b: "", air: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState("");
  const [adj, setAdj] = useState({ amount_inr: "", kind: "credit", note: "" });
  const [parentId, setParentId] = useState("");
  const [rateCard, setRateCard] = useState(null);
  const [rcForm, setRcForm] = useState(null);
  const [rcEdit, setRcEdit] = useState(false);
  const [invRange, setInvRange] = useState({ start: "", end: "" });

  const load = () =>
    appApi.get(`admin/users/${id}`).then((d) => {
      const user = d.user || d;
      setData({ ...d, user });
      const cm = user.custom_markup || {};
      setMarkup({ b2c: cm.b2c ?? "", b2b: cm.b2b ?? "", air: cm.air ?? "" });
    }).catch((e) => setData({ error: formatApiErrorDetail(e.response?.data?.detail) }));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    appApi.get(`admin/users/${id}/rate-card`).then((d) => {
      setRateCard(d);
      const c = d.card || {};
      setRcForm({
        enabled: d.enabled === true,
        billing_name: d.billing_name || "",
        billing_gstin: d.billing_gstin || "",
        billing_address: d.billing_address || "",
        divisor: c.divisor ?? 5000,
        oda_per_kg: c.oda_per_kg ?? 0,
        gst_pct: c.gst_pct ?? 0,
        zones: JSON.parse(JSON.stringify(c.zones || {})),
      });
    }).catch(() => setRateCard({ enabled: false }));
    // eslint-disable-next-line
  }, [id]);

  const setRc = (k) => (e) => setRcForm((f) => ({ ...f, [k]: e.target.value }));
  const setRcZone = (z, k) => (e) =>
    setRcForm((f) => ({ ...f, zones: { ...f.zones, [z]: { ...f.zones[z], [k]: e.target.value } } }));
  const setRcSlab = (z, i) => (e) =>
    setRcForm((f) => {
      const slabs = [...(f.zones[z].slabs || [])];
      slabs[i] = e.target.value;
      return { ...f, zones: { ...f.zones, [z]: { ...f.zones[z], slabs } } };
    });

  const saveRateCard = async () => {
    if (!window.confirm(`Update the contracted rate card for ${u.name || u.email}? New rates apply to their very next quote — on the website and the app.`)) return;
    setBusy(true);
    try {
      const num = (v) => (v === "" || v == null ? 0 : Number(v));
      const zones = {};
      for (const [z, zd] of Object.entries(rcForm.zones || {})) {
        zones[z] = {
          label: zd.label || "",
          first_500: num(zd.first_500),
          add_1kg: num(zd.add_1kg),
          slabs: (zd.slabs || []).map(num),
        };
      }
      await appApi.put(`admin/users/${id}/rate-card`, {
        enabled: rcForm.enabled,
        card: { divisor: num(rcForm.divisor), oda_per_kg: num(rcForm.oda_per_kg), gst_pct: num(rcForm.gst_pct), zones },
        billing_name: rcForm.billing_name,
        billing_address: rcForm.billing_address,
        billing_gstin: rcForm.billing_gstin,
      });
      toast.success("Rate card saved — live on the app and website.");
      setRcEdit(false);
      const d = await appApi.get(`admin/users/${id}/rate-card`);
      setRateCard(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invRange.start) || !/^\d{4}-\d{2}-\d{2}$/.test(invRange.end)) {
      toast.error("Dates must be YYYY-MM-DD.");
      return;
    }
    if (!window.confirm(`Generate an invoice for ${u.name || u.email} covering ${invRange.start} → ${invRange.end}?`)) return;
    setBusy(true);
    try {
      const d = await appApi.post("admin/invoices", { user_id: String(id), start: invRange.start, end: invRange.end });
      toast.success(`Invoice ${d.number || d.id} created (₹${d.total ?? "—"}).`);
      setInvRange({ start: "", end: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="merchant-detail-loading">Loading merchant…</p>;
  if (data.error)
    return <p className="text-sm font-semibold text-red-600" data-testid="merchant-detail-error">{data.error}</p>;

  const u = data.user;
  const deactivated = u.deactivated === true || u.status === "deactivated";

  const deactivate = async () => {
    if (!window.confirm(`Deactivate ${u.email}? They will be blocked from login immediately. History is preserved.`)) return;
    setBusy(true);
    try {
      await appApi.del(`admin/users/${id}`);
      toast.success("Merchant deactivated.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await appApi.post(`admin/users/${id}/restore`);
      toast.success("Merchant restored.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveMarkup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await appApi.patch(`admin/users/${id}/markup`, {
        b2c: parseFloat(markup.b2c) || 0,
        b2b: parseFloat(markup.b2b) || 0,
        air: parseFloat(markup.air) || 0,
      });
      toast.success("Custom markup saved.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Generate a temporary password for ${u.email}? They will be forced to change it on next login.`)) return;
    setBusy(true);
    setTempPw("");
    try {
      const d = await appApi.post(`admin/users/${id}/reset-password`);
      const tp = d.temporary_password || d.temp_password || d.password || "";
      setTempPw(tp);
      toast.success(tp ? "Temporary password generated." : "Password reset — ask the merchant to use forgot/change flow.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const walletAdjust = async (e) => {
    e.preventDefault();
    if (!adj.note.trim()) {
      toast.error("Add a note (audit trail).");
      return;
    }
    if (!window.confirm(`${adj.kind === "credit" ? "Credit" : "Debit"} ${inr(parseFloat(adj.amount_inr))} ${adj.kind === "credit" ? "to" : "from"} ${u.email}'s wallet?`)) return;
    setBusy(true);
    try {
      await appApi.post(`admin/users/${id}/wallet-adjust`, {
        amount_inr: parseFloat(adj.amount_inr),
        kind: adj.kind,
        note: adj.note.trim(),
      });
      toast.success(`Wallet ${adj.kind === "credit" ? "credited" : "debited"}.`);
      setAdj({ amount_inr: "", kind: "credit", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleFlag = async (flag, enable, onFn, offFn, label) => {
    setBusy(true);
    try {
      if (enable) await appApi.post(onFn);
      else await appApi.del(offFn);
      toast.success(`${label} ${enable ? "enabled" : "disabled"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const assignParent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await appApi.post(`admin/users/${id}/assign-parent`, { parent_user_id: parentId.trim() });
      toast.success("Parent reseller linked.");
      setParentId("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const unassignParent = async () => {
    setBusy(true);
    try {
      await appApi.del(`admin/users/${id}/parent`);
      toast.success("Parent reseller unlinked.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    { label: "Email", value: u.email },
    { label: "Business", value: u.business || "—" },
    { label: "Joined", value: fmtDateTime(u.created_at) },
    { label: "Wallet", value: u.wallet_inr != null ? inr(u.wallet_inr) : "—" },
    { label: "Orders", value: u.orders_count ?? "—" },
    { label: "GMV", value: u.gmv_inr != null ? inr(u.gmv_inr) : "—" },
    { label: "KYC approved by", value: u.kyc_approved_by || "—" },
    { label: "KYC approved at", value: fmtDateTime(u.kyc_approved_at) },
  ];

  return (
    <div data-testid="merchant-detail-page" className="space-y-6 max-w-4xl">
      <Link to="/admin/merchants" data-testid="merchant-detail-back" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-obsidian">
        <ArrowLeft className="w-4 h-4" /> All merchants
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl">{u.name || "—"}</h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={u.kyc_status || "pending"} />
            {deactivated && <StatusBadge status="cancelled" />}
          </div>
        </div>
        {deactivated ? (
          <button onClick={restore} disabled={busy} data-testid="merchant-restore-button"
            className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
            <RotateCcw className="w-4 h-4" /> Restore Merchant
          </button>
        ) : (
          <button onClick={deactivate} disabled={busy} data-testid="merchant-deactivate-button"
            className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
            <Ban className="w-4 h-4" /> Deactivate
          </button>
        )}
      </div>

      <div className="border-2 border-obsidian bg-white" data-testid="merchant-profile">
        {rows.map((r, i) => (
          <div key={r.label} className={`flex justify-between items-center px-6 py-4 gap-4 ${i !== rows.length - 1 ? "border-b border-zinc-100" : ""}`}>
            <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{r.label}</span>
            <span className="text-sm font-semibold text-right">{r.value}</span>
          </div>
        ))}
      </div>

      <form onSubmit={saveMarkup} className="border-2 border-obsidian bg-white p-6" data-testid="merchant-markup-form">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Percent className="w-4 h-4 text-blaze" /> Custom markup % (overrides global)
        </p>
        <div className="grid grid-cols-3 gap-4 mt-4">
          {["b2c", "b2b", "air"].map((k) => (
            <div key={k}>
              <label htmlFor={`markup-${k}`} className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{k.toUpperCase()} %</label>
              <input id={`markup-${k}`} data-testid={`markup-${k}-input`} type="number" min="0" step="0.5" value={markup[k]}
                onChange={(e) => setMarkup((m) => ({ ...m, [k]: e.target.value }))}
                className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
            </div>
          ))}
        </div>
        <button type="submit" disabled={busy} data-testid="markup-save-button"
          className="mt-5 bg-obsidian text-sky-300 font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          {busy ? "Saving…" : "Save Markup"}
        </button>
      </form>

      {/* rate card (contract rates) */}
      {rateCard && (
        <div className="space-y-3" data-testid="merchant-ratecard">
          <div className="flex items-center gap-3">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Rate card</p>
            <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 border-2 border-obsidian ${rateCard.enabled ? "bg-acid text-white" : "bg-zinc-100 text-zinc-400"}`} data-testid="merchant-ratecard-state">
              {rateCard.enabled ? "Active" : "Not enabled"}
            </span>
          </div>
          {rateCard.enabled && <RateChart card={rateCard.card || {}} />}
          {(rateCard.billing_name || rateCard.billing_gstin || rateCard.billing_address) && (
            <p className="text-xs font-mono text-zinc-500" data-testid="merchant-ratecard-billing">
              Billing: <span className="font-bold text-obsidian">{rateCard.billing_name || "—"}</span>
              {rateCard.billing_gstin ? ` · GSTIN ${rateCard.billing_gstin}` : ""}
              {rateCard.billing_address ? ` · ${rateCard.billing_address}` : ""}
            </p>
          )}

          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setRcEdit((v) => !v)} data-testid="ratecard-edit-toggle"
              className="text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2 border-2 border-obsidian bg-obsidian text-sky-300 hover:translate-y-[2px] transition-all duration-150">
              {rcEdit ? "Close editor" : "Edit rates"}
            </button>
          </div>

          {rcEdit && rcForm && (
            <div className="border-2 border-obsidian bg-white" data-testid="ratecard-editor">
              <div className="px-5 py-4 border-b-2 border-obsidian flex items-center justify-between flex-wrap gap-3">
                <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Edit contracted rate card</p>
                <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                  <input type="checkbox" checked={rcForm.enabled} onChange={(e) => setRcForm((f) => ({ ...f, enabled: e.target.checked }))}
                    data-testid="ratecard-enabled-toggle" className="w-4 h-4 accent-blue-600" />
                  Rate card active
                </label>
              </div>

              <div className="p-5 space-y-5">
                <div className="grid grid-cols-3 gap-3 max-w-xl">
                  {[
                    { k: "divisor", label: "Volumetric divisor" },
                    { k: "oda_per_kg", label: "ODA ₹/kg" },
                    { k: "gst_pct", label: "GST %" },
                  ].map((f) => (
                    <div key={f.k}>
                      <label className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">{f.label}</label>
                      <input type="number" min="0" step="0.1" value={rcForm[f.k]} onChange={setRc(f.k)} data-testid={`ratecard-edit-${f.k}`}
                        className="mt-1 w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
                    </div>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[900px]" data-testid="ratecard-edit-table">
                    <thead>
                      <tr className="text-left border-b-2 border-obsidian">
                        {["Zone", "Region label", "First 500g ₹", "Add'l 1kg ₹", ...[50, 100, 300, 500, 1000, 2000, "2000+"].map((s) => `≤${s}kg/kg`)].map((h) => (
                          <th key={h} className="px-2 py-2 text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(rcForm.zones || {}).map((z) => (
                        <tr key={z} className="border-b border-zinc-100" data-testid={`ratecard-edit-zone-${z}`}>
                          <td className="px-2 py-2 font-display font-black text-acid text-base">{z}</td>
                          <td className="px-2 py-2">
                            <input value={rcForm.zones[z].label || ""} onChange={setRcZone(z, "label")} data-testid={`ratecard-edit-zone-${z}-label`}
                              className="w-40 border-2 border-obsidian bg-paper px-2 py-1.5 outline-none" />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" min="0" step="0.5" value={rcForm.zones[z].first_500 ?? ""} onChange={setRcZone(z, "first_500")} data-testid={`ratecard-edit-zone-${z}-first`}
                              className="w-20 border-2 border-obsidian bg-paper px-2 py-1.5 outline-none" />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" min="0" step="0.5" value={rcForm.zones[z].add_1kg ?? ""} onChange={setRcZone(z, "add_1kg")} data-testid={`ratecard-edit-zone-${z}-addkg`}
                              className="w-20 border-2 border-obsidian bg-paper px-2 py-1.5 outline-none" />
                          </td>
                          {(rcForm.zones[z].slabs || []).map((s, i) => (
                            <td key={i} className="px-2 py-2">
                              <input type="number" min="0" step="0.5" value={s} onChange={setRcSlab(z, i)} data-testid={`ratecard-edit-zone-${z}-slab-${i}`}
                                className="w-16 border-2 border-obsidian bg-paper px-2 py-1.5 outline-none" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  <input value={rcForm.billing_name} onChange={setRc("billing_name")} placeholder="Billing name" data-testid="ratecard-edit-billing-name"
                    className="border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
                  <input value={rcForm.billing_gstin} onChange={setRc("billing_gstin")} placeholder="Billing GSTIN" data-testid="ratecard-edit-billing-gstin"
                    className="border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
                  <input value={rcForm.billing_address} onChange={setRc("billing_address")} placeholder="Billing address" data-testid="ratecard-edit-billing-address"
                    className="border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
                </div>

                <button onClick={saveRateCard} disabled={busy} data-testid="ratecard-save"
                  className="bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60 text-xs uppercase tracking-widest">
                  {busy ? "Saving…" : "Save rate card"}
                </button>
              </div>
            </div>
          )}

          {/* invoice generator */}
          <form onSubmit={createInvoice} className="border-2 border-obsidian bg-white p-5 flex items-end gap-3 flex-wrap" data-testid="ratecard-invoice-form">
            <p className="w-full text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Generate invoice for this merchant</p>
            <div>
              <label className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">From</label>
              <input type="date" required value={invRange.start} onChange={(e) => setInvRange((r) => ({ ...r, start: e.target.value }))} data-testid="invoice-start"
                className="mt-1 block border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">To</label>
              <input type="date" required value={invRange.end} onChange={(e) => setInvRange((r) => ({ ...r, end: e.target.value }))} data-testid="invoice-end"
                className="mt-1 block border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
            </div>
            <button type="submit" disabled={busy} data-testid="invoice-generate"
              className="bg-obsidian text-sky-300 font-bold px-6 py-2.5 border-2 border-obsidian text-xs uppercase tracking-widest disabled:opacity-60">
              {busy ? "Generating…" : "Generate invoice"}
            </button>
          </form>
        </div>
      )}

      {/* feature flags */}
      <div className="border-2 border-obsidian bg-white p-6" data-testid="merchant-flags">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Access & program flags</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          {[
            { key: "reseller", label: "Reseller program", on: u.reseller_enabled === true, onFn: `admin/users/${id}/enable-reseller`, offFn: `admin/users/${id}/disable-reseller`, testid: "flag-reseller" },
            { key: "tracker", label: "Tracker access", on: u.is_tracker === true, onFn: `admin/users/${id}/tracker`, offFn: `admin/users/${id}/tracker`, testid: "flag-tracker" },
            { key: "b2bop", label: "B2B tracking operator", on: u.can_edit_b2b_tracking === true, onFn: `admin/users/${id}/b2b-tracking-operator`, offFn: `admin/users/${id}/b2b-tracking-operator`, testid: "flag-b2b-operator" },
          ].map((f) => (
            <div key={f.key} className="border-2 border-obsidian p-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-bold text-sm">{f.label}</p>
                <p className={`text-[10px] font-mono font-bold uppercase tracking-widest mt-1 ${f.on ? "text-green-600" : "text-zinc-400"}`} data-testid={`${f.testid}-state`}>
                  {f.on ? "Enabled" : "Disabled"}
                </p>
              </div>
              <button onClick={() => toggleFlag(f.key, !f.on, f.onFn, f.offFn, f.label)} disabled={busy} data-testid={f.testid}
                className={`text-[11px] font-bold uppercase tracking-widest px-4 py-2 border-2 border-obsidian transition-colors duration-150 disabled:opacity-60 ${f.on ? "bg-white text-red-600 hover:bg-red-50" : "bg-acid text-white hover:bg-obsidian"}`}>
                {f.on ? "Disable" : "Enable"}
              </button>
            </div>
          ))}
        </div>
        {u.parent_user_id && (
          <p className="mt-4 text-xs font-mono text-zinc-500" data-testid="merchant-parent">
            Linked under reseller: <span className="font-bold text-obsidian">{u.parent_user_id}</span>
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        {/* reset password */}
        <div className="border-2 border-obsidian bg-white p-6" data-testid="merchant-reset-password">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blaze" /> Locked out?
          </p>
          <p className="text-xs text-zinc-500 mt-2">Generate a temporary password if the merchant is locked out. They must change it on next login.</p>
          <button onClick={resetPassword} disabled={busy} data-testid="merchant-reset-password-button"
            className="mt-4 bg-obsidian text-sky-300 font-bold px-6 py-2.5 border-2 border-obsidian text-xs uppercase tracking-widest hover:translate-y-[2px] transition-all duration-150 disabled:opacity-60">
            Generate temporary password
          </button>
          {tempPw && (
            <div className="mt-4 border-2 border-obsidian bg-acid/20 px-4 py-3 flex items-center justify-between gap-3" data-testid="merchant-temp-password">
              <code className="font-mono font-bold text-sm">{tempPw}</code>
              <button onClick={() => { navigator.clipboard?.writeText(tempPw); toast.success("Copied."); }} aria-label="Copy temporary password"
                data-testid="merchant-temp-password-copy" className="p-2 border-2 border-obsidian hover:bg-acid hover:text-white transition-colors duration-150">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* wallet adjust */}
        <form onSubmit={walletAdjust} className="border-2 border-obsidian bg-white p-6" data-testid="merchant-wallet-adjust">
          <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-blaze" /> Wallet adjustment
          </p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <select value={adj.kind} onChange={(e) => setAdj((a) => ({ ...a, kind: e.target.value }))} data-testid="wallet-adjust-kind"
              className="border-2 border-obsidian bg-paper px-3 py-2.5 text-sm outline-none">
              <option value="credit">Credit (+)</option>
              <option value="debit">Debit (−)</option>
            </select>
            <input required type="number" min="1" step="0.01" value={adj.amount_inr} onChange={(e) => setAdj((a) => ({ ...a, amount_inr: e.target.value }))}
              placeholder="Amount ₹" data-testid="wallet-adjust-amount"
              className="border-2 border-obsidian bg-paper px-3 py-2.5 text-sm outline-none" />
            <input required value={adj.note} onChange={(e) => setAdj((a) => ({ ...a, note: e.target.value }))}
              placeholder="Note (audit trail)" data-testid="wallet-adjust-note"
              className="col-span-2 border-2 border-obsidian bg-paper px-3 py-2.5 text-sm outline-none" />
          </div>
          <button type="submit" disabled={busy} data-testid="wallet-adjust-submit"
            className="mt-4 bg-acid text-white font-bold px-6 py-2.5 border-2 border-obsidian text-xs uppercase tracking-widest hover:bg-obsidian transition-colors duration-150 disabled:opacity-60">
            Apply adjustment
          </button>
        </form>
      </div>

      {/* assign parent reseller */}
      <form onSubmit={assignParent} className="border-2 border-obsidian bg-white p-6" data-testid="merchant-assign-parent">
        <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-blaze" /> Link under a reseller
        </p>
        <div className="flex gap-3 mt-4 items-end flex-wrap">
          <input value={parentId} onChange={(e) => setParentId(e.target.value)} placeholder="Reseller user ID" data-testid="assign-parent-input"
            className="flex-1 min-w-64 border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          <button type="submit" disabled={busy || !parentId.trim()} data-testid="assign-parent-submit"
            className="bg-obsidian text-sky-300 font-bold px-6 py-2.5 border-2 border-obsidian text-xs uppercase tracking-widest disabled:opacity-60">
            Link
          </button>
          {u.parent_user_id && (
            <button type="button" onClick={unassignParent} disabled={busy} data-testid="unassign-parent-button"
              className="inline-flex items-center gap-1.5 bg-white text-red-600 font-bold px-6 py-2.5 border-2 border-red-600 text-xs uppercase tracking-widest hover:bg-red-50 disabled:opacity-60">
              <Unlink className="w-3.5 h-3.5" /> Unlink
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
